import { cognitoConfig } from "@src/appV2/Auth/cognito/config";
import { DeploymentEnvironmentName, environmentConfig } from "@src/appV2/environment";
import { type ReactElement, useEffect, useState } from "react";

const BLOCKED_ENVIRONMENTS = new Set<string>([
  DeploymentEnvironmentName.PRODUCTION,
  DeploymentEnvironmentName.PROD_SHADOW,
]);

/**
 * E2E-only page that injects Cognito tokens into localStorage,
 * making the app authenticated without going through the OTP flow.
 *
 * Tokens are passed via the URL hash to avoid logging in server access logs.
 * Expected hash format: #idToken=<jwt>&accessToken=<jwt>&refreshToken=<jwt>
 *
 * After injection, the page reloads so Amplify picks up the session.
 *
 * Blocked on production and prod-shadow environments for security.
 */
export function E2EInjectSessionPage(): ReactElement {
  const [status, setStatus] = useState("Injecting session...");

  useEffect(() => {
    const environment = environmentConfig.REACT_APP_ENVIRONMENT_NAME;
    if (BLOCKED_ENVIRONMENTS.has(environment)) {
      setStatus("Error: This page is not available in production environments");
      return;
    }

    try {
      const hash = window.location.hash.slice(1);
      const params = new URLSearchParams(hash);

      const idToken = params.get("idToken");
      const accessToken = params.get("accessToken");
      const refreshToken = params.get("refreshToken");

      if (!idToken || !accessToken || !refreshToken) {
        setStatus("Error: Missing tokens in URL hash");
        return;
      }

      // Decode the idToken to extract the Cognito username (sub claim)
      const payload = JSON.parse(atob(idToken.split(".")[1]));
      const username: string = payload.sub;

      if (!username) {
        setStatus("Error: Could not extract username from idToken");
        return;
      }

      const { clientId } = cognitoConfig;
      const prefix = `CognitoIdentityServiceProvider.${clientId}`;

      localStorage.setItem(`${prefix}.${username}.idToken`, idToken);
      localStorage.setItem(`${prefix}.${username}.accessToken`, accessToken);
      localStorage.setItem(`${prefix}.${username}.refreshToken`, refreshToken);
      localStorage.setItem(`${prefix}.LastAuthUser`, username);

      // Navigate to home to trigger Amplify session detection
      window.location.replace("/home");
    } catch (error) {
      setStatus(`Error: ${String(error)}`);
    }
  }, []);

  return <div data-testid="e2e-inject-session">{status}</div>;
}
